<script setup>
import NavIndicator from '@/components/partials/NavIndicator.vue';
import { defineProps, computed } from 'vue';
import dayjs from 'dayjs';

const props = defineProps({
  featuredImage: {
    type: String,
    required: true,
  },
  featuredImageAltText: {
    type: String,
    required: false,
  },
  title: {
    type: String,
    required: true,
  },
  tags: {
    type: Array,
    required: false,
  },
  publishedDate: {
    type: String,
    required: true,
  },
  maxReadTime: {
    type: Number,
    required: true,
  },
});

const navIndicator = computed(() => [
  { text: 'Home', icon: 'fas fa-home', link: '/' },
  { text: 'Blogs and Insights', link: '/blogs-and-insights' },
  { text: props.title || '', link: '' },
]);
</script>
<template>
  <div class="banner">
    <div class="v-container d-flex flex-column ga-8 py-8 banner-container">
      <NavIndicator :navIndicator="navIndicator" v-if="title" />
      <v-row>
        <v-col cols="12" md="6">
          <img
            :src="featuredImage"
            :alt="featuredImageAltText || title"
            class="featured-image"
        /></v-col>
        <v-col
          cols="12"
          md="6"
          class="d-flex flex-column justify-center pl-lg-16"
        >
          <div class="w-100 d-flex flex-column ga-4">
            <div class="d-flex ga-1">
              <span v-for="tag in tags" :key="tag" class="tag">{{ tag }}</span>
            </div>
            <h1 class="text-left title">{{ title }}</h1>
            <div class="d-flex ga-8 align-center w-100 summary">
              <span class="d-flex ga-1 align-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21.6 2.9H19.2V1.7C19.2 1.38174 19.0736 1.07652 18.8485 0.851472C18.6235 0.626428 18.3183 0.5 18 0.5C17.6817 0.5 17.3765 0.626428 17.1515 0.851472C16.9264 1.07652 16.8 1.38174 16.8 1.7V2.9H13.2V1.7C13.2 1.38174 13.0736 1.07652 12.8485 0.851472C12.6235 0.626428 12.3183 0.5 12 0.5C11.6817 0.5 11.3765 0.626428 11.1515 0.851472C10.9264 1.07652 10.8 1.38174 10.8 1.7V2.9H7.2V1.7C7.2 1.38174 7.07357 1.07652 6.84853 0.851472C6.62348 0.626428 6.31826 0.5 6 0.5C5.68174 0.5 5.37652 0.626428 5.15147 0.851472C4.92643 1.07652 4.8 1.38174 4.8 1.7V2.9H2.4C1.76348 2.9 1.15303 3.15286 0.702944 3.60294C0.252856 4.05303 0 4.66348 0 5.3V22.1C0 22.7365 0.252856 23.347 0.702944 23.7971C1.15303 24.2471 1.76348 24.5 2.4 24.5H21.6C22.2365 24.5 22.847 24.2471 23.2971 23.7971C23.7471 23.347 24 22.7365 24 22.1V5.3C24 4.66348 23.7471 4.05303 23.2971 3.60294C22.847 3.15286 22.2365 2.9 21.6 2.9ZM4.8 5.3C4.8 5.61826 4.92643 5.92348 5.15147 6.14853C5.37652 6.37357 5.68174 6.5 6 6.5C6.31826 6.5 6.62348 6.37357 6.84853 6.14853C7.07357 5.92348 7.2 5.61826 7.2 5.3H10.8C10.8 5.61826 10.9264 5.92348 11.1515 6.14853C11.3765 6.37357 11.6817 6.5 12 6.5C12.3183 6.5 12.6235 6.37357 12.8485 6.14853C13.0736 5.92348 13.2 5.61826 13.2 5.3H16.8C16.8 5.61826 16.9264 5.92348 17.1515 6.14853C17.3765 6.37357 17.6817 6.5 18 6.5C18.3183 6.5 18.6235 6.37357 18.8485 6.14853C19.0736 5.92348 19.2 5.61826 19.2 5.3H21.6V7.7H2.4V5.3H4.8ZM2.4 22.1V10.1H21.6V22.1H2.4Z"
                    fill="#686868"
                  />
                  <path
                    d="M18 12.5H6C5.68174 12.5 5.37652 12.6264 5.15147 12.8515C4.92643 13.0765 4.8 13.3817 4.8 13.7C4.8 14.0183 4.92643 14.3235 5.15147 14.5485C5.37652 14.7736 5.68174 14.9 6 14.9H18C18.3183 14.9 18.6235 14.7736 18.8485 14.5485C19.0736 14.3235 19.2 14.0183 19.2 13.7C19.2 13.3817 19.0736 13.0765 18.8485 12.8515C18.6235 12.6264 18.3183 12.5 18 12.5Z"
                    fill="#686868"
                  />
                </svg>
                <p>{{ dayjs(publishedDate).format('MMM D, YYYY') }}</p>
              </span>
              <span class="d-flex ga-1 align-center"
                ><svg
                  class="clock-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
                <p>Max {{ maxReadTime }} min. read</p></span
              >
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<style scoped>
.banner {
  background-color: #f2f2f2;
  padding: 0 6rem;
}
.banner-container {
  max-width: 1280px;
}
.featured-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
  aspect-ratio: 1.77777;
}
.title {
  font-size: clamp(28px, 5vw, 42px);
}
.summary {
  color: #686868;
}
.tag {
  display: inline-block;
  background-color: #e0e7ff;
  color: #4b5eaa;
  padding: 4px 16px;
  border-radius: 12px;
  font-size: 0.8em;
}
@media only screen and (max-width: 1280px){
  .banner {
    padding: 0 5rem;
  }
}
@media only screen and (max-width: 1050px){
  .banner {
    padding: 0 4rem;
  }
}
@media only screen and (max-width: 840px){
  .banner {
    padding: 0 3rem;
  }
}
@media only screen and (max-width: 640px){
  .banner {
    padding: 0 2rem;
  }
}
@media only screen and (max-width: 470px){
  .banner {
    padding: 0 1rem;
  }
}
</style>
